/* 
 *  Copyright (c) Intel Corporation.  All Rights Reserved.
 *
 * Primary entry point for accessing DataSets
 */
/*--!!!!!ATTENTION!!!!!-----DO NOT DELETE code inside Webpack Macros,it will be used in Analytics SDK for Web */

// #IFDEF WebPack

import { logNormal, logInformation, logWarning, logError, logDebug, logConsole, logRawTrack, logTransports, logAutomationError } from "./bootstrap_web_ext";
import {JSONManager , ModuleManager, StorageManager, globalEngine} from "./Init";
import { engine } from "./engine";

// #ENDIF WebPack
function CreateDataset()
{

    /* Dataset object is responsible of querying a set of items as described in the datasets_catalog
    *   Dataset object is only used by the dataset_manager
    */
    function Dataset(name)
    {
        this._name = name;
        if (!this._name) { throw 'Dataset created with no name provided'; }
    }

    Dataset.prototype =
   {

       /* Initializes the dataset with corresponding config
       *
       *  PARAMS:
       *    config: object {'data_items': array of items that belongs in the dataset, 'refresh': refres configuration}
       *  RETURN:
       *    true if successful, false otherwise
       */
       "initialize": function (config)
       {
           try
           {
               if (!config)
               {
                   this._logError('No configuration defined');
                   return false;
               }

               var data_items = config['data_items'];
               if (!data_items)
               {
                   this._logError('Invalid Data items. Config (' + JSON.stringify(config) + ')');
                   return false;
               }
               this._itemsList = data_items;

               var refreshConfig = config['refresh'];
               this._setRefresh(refreshConfig);

               this._logInformation('Initialization complete');
               return true;
           }
           catch (e)
           {
               this._logError('initialize: ' + e.message);
               return false;
           }
       },

       /* Retrieves a datapoint from the dataset
       * PARAMS:
       *    key: target value key
       * RETURN:
       *    _content[key]
       */
       "get": function (key)
       {
           try
           {
               return this.getContent()[key];
           }
           catch (e)
           {
               this._logError('get: ' + e.message);
           }
       },

       /* Retrieves key value pairs for all items that belong to the datasets
       *    Uses the datastore to collect the itemsList
       *    If the dataset is dirty, the dataset will ask the datastore to mark the datapoints as expired
       *
       * RETURN:
       *    JSON object
       */
       "getContent": function ()
       {
           try
           {
               this._logInformation('getContent starting');
               this._logInformation('itemsList' + JSON.stringify(this._itemsList));

               var data_collector = ModuleManager.getSingleton("data_collector");

               if (this.dirty)
               {
                   data_collector.markDataExpired(this._itemsList);
                   this.dirty = false;
               }

               return data_collector.get(this._itemsList);
           }
           catch (e)
           {
               this._logError('getContent: ' + e.message);
           }
       },

         /* Checks if dataset is dirty
         * RETURN:
         *  true if one or more elements expired, false otherwise
         *
         */
        "isDirty": function ()
        {
            try
            {
               if (this.dirty)
               {
                  return true;
               }
               var data_collector = ModuleManager.getSingleton("data_collector");
               return data_collector.isExpired(this._itemsList);
            }
            catch (e)
            {
                this._logError('isExpired: ' + e.message);
                return false;
            }
        },


        /**********************************************************************************************
        ***********************************************************************************************
                Private Functions
        ***********************************************************************************************
        ***********************************************************************************************/

       /* Sets when to set the dirty flag
       *    Allows subscribing to messages on the msg bus
       *    Allows creating timeouts to refresh on
       * 
       * PARAMS:
       *    refreshConfig: object {timeoutInterval: timeout to reset the dataset, onMessageBusMsg: array of msgs that will trigger marking the dataset to dirty}
       */
       "_setRefresh": function (refreshConfig)
       {
           if (!refreshConfig)
           {
               return;
           }

           this._createDatasetRefreshTimer(refreshConfig, this);
       },

        /* Creates to a timeout to set the dirty flag
         * 
         * PARAMS:
         *    refreshConfig: {
         *                      useEngineDefaultTimeout: if true, the timeoutInterval is ignored
         *                      timeoutInterval: time out to refresh dataset
         *                  }
         */
       "_createDatasetRefreshTimer": function (refreshConfig, dataset)
       {
           var useEngineDefaultTimeout = refreshConfig['useEngineDefaultTimeout'];
           var timeoutInterval = refreshConfig['timeoutInterval'];

           if (useEngineDefaultTimeout)
           {
               timeoutInterval = engine.datasetsRefreshRate;
           }

           if (!timeoutInterval)
           {
               return;
           }

           setInterval(timeoutInterval, function ()
           {
               try
               {
                   dataset.dirty = true;
                   dataset._logInformation( 'Marking dirty flag to true');
               }
               catch (e)
               { 
                logError('_createDatasetRefreshTimer: ' + e.message);
               }
           });
       },
       "dirty": true,       // Denotes the datapoints in the dataset is dirty and needs to be refreshed
       "_itemsList": [],    // Items' names that belong to the datasets

       "_logError": function (msg) { logError('dataset(' + this._name + '): ' + msg); },
       "_logInformation": function (msg) { logInformation('dataset(' + this._name + '): ' + msg); }
   };

    var _datasetsManager =
  {

      /* Sets up the dataset_manager by reading datasets_catalog.json
        */
      "setup": function()
      {
          this._logInformation('Setup Started');
          try
          {
              this.datasets_catalog = JSONManager.create("datasets_catalog");
              this._createWatchList();
          }
          catch (e)
          {
              logError('datasets_catalog: Setup failed: ' + e.message);
              return;
          }
          this._logInformation('Setup Complete');
      },

      /* Retrieves a specific Dataset object
        * PARAMS:
        *   name: target dataset name
        * RETURN: Dataset object
        */
      "get": function (name)
      {
          var dataset = this._create(name);
          if (dataset.isDirty())
          {
              this._checkForChange(name, dataset);
          }
          return dataset;
      },

      "_createWatchList": function()
      {
        for(var dataset in this._preconfigured_detections)
        {
          if(this._watchList[dataset]==null)
          {
            this._watchList[dataset] = [];
          }
          for(var idx in this._preconfigured_detections[dataset])
          {
            var key = this._preconfigured_detections[dataset][idx]['key'];
            this._watchList[dataset].push(key);
          }
        }
      },

      "_add": function (name, dataset)
      {
          this._datasets[name] = dataset;
      },

      "_create": function (name)
      {
          var targetDataset = this._datasets[name];
          if (targetDataset)
          {
              return targetDataset;
          }

          var targetDataset = new Dataset(name);
          targetDataset.initialize(this.datasets_catalog[name]);
          this._add(name, targetDataset);

          return targetDataset;
      },
      "_checkForChange": function (datasetName, dataset)
      {
          try
          {
            if(engine.aleStaticInegration())
            {
                logWarning('Not checking for datasets change, since this is static ALE integration');
                return;
            }

            if ((datasetName == null) || ( datasetName == undefined))
            {
              return;
            }

            var datasetWatchList = this._watchList[datasetName];

            if ((datasetWatchList==null) || ( datasetWatchList == undefined))
            {
              // logNormal('No watch list information for dataset: ' + datasetName);
              return;
            }


            var storedObjName = 'analytics_dataset_' + datasetName + '_content';
            var oldDatasetContent = this._cachedData[storedObjName];
            if(!oldDatasetContent)
            {
              oldDatasetContent = StorageManager.get(storedObjName);
            }
            var newDatasetContent = dataset.getContent();

            if (null == oldDatasetContent)
            {
              // We still want to send an event if this is the first time we collect an attribute
              oldDatasetContent = '{}';
            }
            logDebug('Retrieved watch list information for dataset: ' + datasetName);

            oldDatasetContent = JSON.parse(oldDatasetContent);

            // Only store the elements we marked for watching
            var watchListNewValues = {};
            var escapedRuleMismatchStr = escape('[ruleMismatch]');
            var escapedUnknown = escape('[unknown]');
            var escapedNotAssigned = escape('[not assigned]');
            var escapedBracketNull = escape('[null]');

            var changedValueEvents = []; // A list to hold change value events

            for (var idx in datasetWatchList)
            {
              var key = datasetWatchList[idx];
              if((key == null) || (key == undefined)) continue; // A bug has occured from defining Array.prototype.indexOf which caused null keys

              // If this is the first time we collect the value, oldValue will be null
              var oldValue = oldDatasetContent[key];
              var newValue = escape(newDatasetContent[key]);

              // Don't store new value if it was a ruleMismatch OR unknown, keep the old value
              watchListNewValues[key] = ( ( escapedRuleMismatchStr == newValue) || ( escapedUnknown == newValue)) ? oldValue : newValue;
              
              if( oldValue == newValue ) continue;

              if( escapedRuleMismatchStr == newValue) continue;
              if( escapedRuleMismatchStr == oldValue) continue;

              if( escapedUnknown == newValue) continue;

              if( ( escapedUnknown == oldValue) ||
                  ( escapedNotAssigned == oldValue) ||
                  ( null == oldValue ) ||
                  ( undefined == oldValue ) ||
                  ( "null" == oldValue ) ||
                  ( "undefined" == oldValue ) ||
                  ( escapedBracketNull == oldValue ) )
              {
                oldValue = null;
              }

              //Send event
              var eventInfo = {
                'datasetName': datasetName,
                'key': key,
                'oldValue': oldValue,
                'newValue': newValue
              };

              changedValueEvents.push(eventInfo);
            }

            // In case the dataset got dirty again. Let's store the dataset before sending context changed event
            var cachedData = JSON.stringify(watchListNewValues);
            this._cachedData[storedObjName] = cachedData;
            StorageManager.set(storedObjName, cachedData);
            for (var idx in changedValueEvents)
            {
              this._sendContextChangedEvent(changedValueEvents[idx]);
            }
          }
          catch (e)
          {
              logError('Exception while calling _checkForChange: ' + e.message);
          }

      },
      "_sendContextChangedEvent": function(eventInfo)
      {
          //Code changes are needed to refactor and delete `_sendContextChangedEvent`
        this._sendAnalyticsDetectEvent(eventInfo);

        if ( null == eventInfo.oldValue ) return;

        var record = {
            'UniqueIdentifier': 'analytics_dataset_value_changed',
            'type': 'event',
            'payload': {
            'Tracker.Type': 'event',
            'Event.Category': 'Analytics.Content',
            'Sub.Category': eventInfo.datasetName,
            'Event.Action': 'Value.Changed',
            'Event.Label': eventInfo.key,
            'Hit.Label.1': eventInfo.oldValue,
            'Hit.Label.2': eventInfo.newValue
            }
        };

        logNormal('Detected change for key: ' + eventInfo.key + '. In dataset: ' + eventInfo.datasetName + '. Going to send an event');
        var event_handler = ModuleManager.getSingleton("event_handler");
        event_handler.handleV1Record(record);
      },
      "_sendAnalyticsDetectEvent": function(eventInfo)
      {

        var detections = this._preconfigured_detections[eventInfo.datasetName];
        if(!detections) return false;

        for (var idx in detections)
        {
          try
          {
            var item = detections [idx];
            
            if(item['key'] != eventInfo.key) continue;
            if(item['event_id'] == null || item['event_id'] == '') continue; //This is an error case

            var from_exp = (item['from']!= null) ? new RegExp(item['from']) : null;
            var to_exp = (item['to']!= null) ? new RegExp(item['to']) : null;
            
            if(from_exp != null && !from_exp.test(eventInfo.oldValue) ) continue;
            if(from_exp == null && eventInfo.oldValue != null) continue;
            if(to_exp != null && !to_exp.test(eventInfo.newValue) ) continue;
            if(to_exp == null && eventInfo.newValue != null) continue;

            var record = {
              'UniqueIdentifier': item['event_id'],
              'type': 'event',
              'payload': {
                  'Tracker.Type': 'event',
                  'Event.Category': 'Analytics.Content',
                  'Sub.Category': eventInfo.datasetName,
                  'Event.Action': 'Value.Changed',
                  'Event.Label': eventInfo.key,
                  'Hit.Label.1': eventInfo.oldValue,
                  'Hit.Label.2': eventInfo.newValue
                }
            };

            logNormal('Detected dataset item change flow: ' + eventInfo.key + '. Going to send an event');
            var event_handler = ModuleManager.getSingleton("event_handler");
            event_handler.handleV1Record(record);
            return true;
          }
          catch(e)
          {
            logError('_sendAnalyticsDetectEvent: ' + e.message);
          }
        }
        return false;
      },
      "_cachedData": {},
      "_watchList": {},
      "_preconfigured_detections" :
      {
        "wss":[
                {
                  'event_id': 'analytics_detect_product_wss_paid',
                  'key': 'WSS.IsTrial',
                  'from' : '1',
                  'to': '0'
                },
                {
                  'event_id': 'analytics_detect_product_wss_registered',
                  'key': 'WSS.User.Registered',
                  'from' : 'false',
                  'to': 'true'
                },
                {
                  'event_id': 'analytics_detect_product_csp_client_id_changed',
                  'key': 'WSS.CSP.ClientId',
                  'from' : '.*',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_affiliate_id_changed',
                  'key': 'WSS.Affiliate.ID',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_flex_pkg_id_changed',
                  'key': 'WSS.Flex.Package.ID',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_expiry_dt_changed',
                  'key': 'WSS.Expiry.Date.ISO',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_extended_expiry_dt_changed',
                  'key': 'WSS.Extended.Expiry.Date.ISO',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_product_key_changed',
                  'key': 'WSS.Product.Key',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_wsc_protection_status_changed',
                  'key': 'WSS.WSC.Protection.Status',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_pkg_id_changed',
                  'key': 'WSS.Package.ID',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_product_wss_entitlement_received',
                  'key': 'product_wss_entitlement_status_entitled',
                  'from' : 'false',
                  'to': 'true'
                },
                {
                  'event_id': 'analytics_detect_product_wss_oem_oobe_complete',
                  'key': 'product_wss_oem_oobe_complete',
                  'from' : '(null|0)',
                  'to': '1'
                }
        ],
        "device": [
                {
                  'event_id': 'analytics_detect_device_msft_guid_changed',
                  'key': 'Device.Microsoft.Machine.Guid',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                },
                {
                  'event_id': 'analytics_detect_device_antimalware_enabled_changed',
                  'key': 'Device.Antimalware.Provider.Enabled',
                  'from' : '^((?!^null$).)*$',
                  'to': '.*'
                }
        ],
        "ngm": [
                {
                  'event_id': 'analytics_detect_wss_ngm_enabled',
                  'key': 'NGM.Enabled',
                  'from' : 'false',
                  'to': 'true'
                }
        ]
      },
      "_datasets": {},
      "datasets_catalog": {},
      "_logInformation": function (msg)
      {
          logInformation('_datasetsManager: ' + msg);
      },
      "_logError": function (msg)
      {
          logError('_datasetsManager: ' + msg);
      }

  }; // populated by the dataset scripts


    _datasetsManager.setup();
    return _datasetsManager;
}

ModuleManager.registerFactory("dataset", CreateDataset);
//export  {CreateDataset};
//globalEngine.FactoryMap["dataset"] =  CreateDataset;



